import type { RewardPopupListRes } from "@/types/api/reward/reward-popup-list";
import { RewardStatusEnum, RewardTypeEnum } from "@/types/reward";
import type { RewardDrawerCardItem } from "../types";

// 根据按钮状态：claim > spin > deposit > 其它状态，常态下的排序优先级为：
// 弹窗卡片列表显示顺序： 欢迎礼包 > 日历奖励 > VIP奖励（VIP（晋级）奖励 > 现金返还 > 每日奖励 > 每周奖励 > 每月奖励） > 特别奖励（触发式>累充>奖励解锁） > 其它奖励（裂变转盘奖励 > 邀请奖励 > 排行榜奖励）

// 按卡片类型归类数据，并返回拍平后的排序结果
export function sortRewardCardData(data: RewardPopupListRes['list']) {
  if (!data) return [];
  let cardData: RewardDrawerCardItem[] = data.map((item) => item.cards).flat() as RewardDrawerCardItem[];
  cardData = cardData.filter((item) => item.status !== RewardStatusEnum.CLAIMED);

  // 日历奖励只展示一个，需单独处理：只展示可领取的 & 优先级是：当天(0) > 昨天(-1) > 前天(-2) > 明天(1)，都没有就展示今日无奖励状态
  const allCalendarRewards = cardData.filter((item) => item.type === RewardTypeEnum.CALENDAR_REWARD);
  // 从主列表移除所有日历奖励，处理后再push回去
  cardData = cardData.filter((item) => item.type !== RewardTypeEnum.CALENDAR_REWARD);

  // 可领取
  const claimable = allCalendarRewards.filter((item) => item.status === RewardStatusEnum.CLAIM);
  const priorityMap: Record<number, number> = { 0: 0, [-1]: 1, [-2]: 2, 1: 3 } as const;
  const byPriority = (a: RewardDrawerCardItem, b: RewardDrawerCardItem) => {
    const pa = priorityMap[(a.calendarDay as number) || 99] ?? 99;
    const pb = priorityMap[(b.calendarDay as number) || 99] ?? 99;
    return pa - pb;
  };

  let chosenCalendar: RewardDrawerCardItem | undefined;
  if (claimable.length) {
    chosenCalendar = [...claimable].sort(byPriority)[0];
  } else {
    // 无可领取，则找今日(0)作为“今日无奖励状态”的展示项（若后端提供此卡片）
    const todayCards = allCalendarRewards.filter((item) => item.calendarDay === 0);
    if (todayCards.length) {
      chosenCalendar = todayCards[0];
    }
  }

  if (chosenCalendar) {
    cardData = cardData.concat([chosenCalendar]);
  }
  // 优先按claim > spin > deposit > 其他状态进行排序
  let claimRewardData = cardData.filter((item) => item.status === RewardStatusEnum.CLAIM);
  let spinRewardData = cardData.filter((item) => item.status === RewardStatusEnum.SPIN);
  let depositRewardData = cardData.filter((item) => item.status === RewardStatusEnum.DEPOSIT);
  let otherRewardData = cardData.filter((item) => item.status !== RewardStatusEnum.CLAIM && item.status !== RewardStatusEnum.SPIN && item.status !== RewardStatusEnum.DEPOSIT);

  // 再按卡片类型排序（并拍平）
  claimRewardData = sortRewardDataByType(claimRewardData);
  spinRewardData = sortRewardDataByType(spinRewardData);
  depositRewardData = sortRewardDataByType(depositRewardData);
  otherRewardData = sortRewardDataByType(otherRewardData);

  // 最终拍平并返回
  return [claimRewardData, spinRewardData, depositRewardData, otherRewardData].reduce<RewardDrawerCardItem[]>((acc, cur) => acc.concat(cur), []);
}

function sortRewardDataByType(data: RewardDrawerCardItem[]) {
  // 欢迎礼包(弹窗仅展示奖励加成的)
  const welcomeGift = data.filter((item) => item.type === RewardTypeEnum.WELCOME_GIFT && item.status === RewardStatusEnum.DEPOSIT && (item.bonusPercentage || item.giftPercentage));
  // 日历奖励
  const calendarReward = data.filter((item) => item.type === RewardTypeEnum.CALENDAR_REWARD);
  // VIP奖励（VIP（晋级）奖励 > 现金返还 > 每日奖励 > 每周奖励 > 每月奖励）
  const vipReward = data.filter((item) => item.type === RewardTypeEnum.PROMOTION_REWARD && item.status === RewardStatusEnum.CLAIM).slice(0, 1); // 只取最新的一个=第一个
  const cashBackReward = data.filter((item) => item.type === RewardTypeEnum.CASH_BACK);
  const weeklyReward = data.filter((item) => item.type === RewardTypeEnum.WEEKLY_REWARD);
  const monthlyReward = data.filter((item) => item.type === RewardTypeEnum.MONTHLY_REWARD);
  const dailyReward = data.filter((item) => item.type === RewardTypeEnum.DAILY_REWARD);
  // 特别奖励（触发式（惊喜礼包）>累充>奖励解锁）
  const triggerReward = data.filter((item) => item.type === RewardTypeEnum.TRIGGER_REWARD && item.status === RewardStatusEnum.DEPOSIT);
  const cumulativeReward = data.filter((item) => item.type === RewardTypeEnum.CUMULATIVE_REWARD);
  const rewardUnlock = data.filter((item) => item.type === RewardTypeEnum.REWARD_UNLOCK);
  // 其它奖励（裂变转盘奖励 > 邀请奖励 > 排行榜奖励）
  const spinReward = data.filter((item) => item.type === RewardTypeEnum.TURNTABLE_REWARD);
  // 邀请奖励
  const inviteReward = data.filter((item) => item.type === RewardTypeEnum.INVITE_REWARD);
  // 排行榜奖励
  const rankingReward = data.filter((item) => item.type === RewardTypeEnum.RANKING_REWARD && item.status === RewardStatusEnum.CLAIM);

  // 拍平为一维数组，生成最终排序
  return [
    welcomeGift,
    calendarReward,
    vipReward,
    cashBackReward,
    dailyReward,
    weeklyReward,
    monthlyReward,
    triggerReward,
    cumulativeReward,
    rewardUnlock,
    spinReward,
    inviteReward,
    rankingReward,
  ].reduce<RewardDrawerCardItem[]>((acc, cur) => (cur.length ? acc.concat(cur) : acc), []);
}

export const rewardCardDrawerHelper = {
  sortRewardCardData,
}